import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { StyleSheet, ScrollView } from 'react-native';
import qs from 'qs';
import { compose } from 'redux';
import { Iffy, DNABox, DNACard, DNACheckbox, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import { useLocation, useParams } from 'src/router';
import DNADocumentGridList from 'src/components/DNA/GridList/DNADocumentGridList';
import DNAGrid from 'src/components/DNA/Grid/DNAGrid';
import DNAPagination, { usePagination } from 'src/components/DNA/Pagination/DNAPagination';
import { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty';
import { useDNADocumentFilters, withDNADocumentFilters } from 'src/components/DNA/Document/DNADocumentFilters';
import docQuery, { merge } from 'src/state/redux/document/query';
import { DisplayModes } from 'src/components/DNA/hooks/useDisplayMode';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useSort, withSort } from 'src/components/DNA/hooks/useSort';
import { useAllDocumentsInstance } from 'src/state/redux/selector/document';
import useFixedHeader from '@alucio/lux-ui/src/hooks/useFixedLayout';
import { DocumentORM } from 'src/types/types';
import { useTenant } from 'src/state/redux/selector/tenant';
import ActiveUser from 'src/state/global/ActiveUser';
import QuickFiltersProvider from 'src/components/QuickFilters/QuickFiltersProvider';
import FiltersSidebar from 'src/components/QuickFilters/FiltersSidebar';
import DocumentSelectManager, { useDocumentSelectManager, useLibraryDisplayMode } from './DocumentSelectManager';
import SelectedCollapsibleCard from './SelectedCollapsibleCard';
import { useDocumentSearchV2, useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';
import { useDocumentSearch } from 'src/state/redux/selector/documentSearch/documentSearch';
import ListSkeleton from 'src/components/ListSkeleton/ListSkeleton';
import { useCPMPageRoute } from 'src/components/ContentPreviewModalV2/Util/useCPMDedicatedUrl';
import Header from './Header';
import CenteredGridLayout from 'src/components/CenteredLayout/CenteredGridLayout';
import AISearchResults from 'src/components/Header/SearchInput/aiSearch/AISearchResults';
import { getSearchRouteText } from 'src/utils/searchRouteText';

const styles = StyleSheet.create({
  mobileGridViewContainer: {
    paddingVertical: 16,
    minWidth: '100%',
  },
  mobileGridViewHeader: {
    backgroundColor: colors['color-gray-10'],
    paddingHorizontal: 16,
    paddingVertical: 5,
  },
  selectedFilters: {
    backgroundColor: colors['color-brand2-500'],
    borderRadius: 3,
    left: 20,
    paddingHorizontal: 4,
    paddingVertical: 2,
    position: 'absolute',
    top: 0,
  },
  filtersSidebarScrollView: {
    maxHeight: '100vh',
    minWidth: 420,
    maxWidth: 420,
    position: 'sticky' as any,
    top: 0,
    zIndex: 10,
    paddingBottom: 350,
  },
  filersSidebarNotVisible: {
    display: 'none',
  },
  paginationNav: {
    alignSelf: 'flex-start',
    marginTop: 8,
  },
});

export const useDNALibrary = (searchText?: string, fromPublisher?: boolean, titleSearchText?: string) => {
  const { displayMode, displayModeIconName, toggleDisplayMode } = useLibraryDisplayMode();
  const { sortSelectorOpts } = useSort()
  const { filterSelectorOpts, unpublishedToggle } = useDNADocumentFilters()

  const filteredDocsSelectorOpts = useMemo(
    () => docQuery.merge(
      docQuery.filters.published, filterSelectorOpts, sortSelectorOpts),
    [sortSelectorOpts, filterSelectorOpts],
  )

  const allDocsSelectorOpts = useMemo(
    () => fromPublisher
      ? unpublishedToggle
        ? docQuery.merge(docQuery.filters.hasUnpublishedVersion, sortSelectorOpts)
        : docQuery.merge(sortSelectorOpts)
      : docQuery.merge(
        docQuery.filters.published, sortSelectorOpts),
    [sortSelectorOpts, filterSelectorOpts],
  )

  const { query } = useDocumentSearchV2(searchText);

  const isSearch = !!searchText?.length
  const trimmedTitleSearchText = !isSearch ? titleSearchText?.trim() ?? '' : '';
  const isTitleSearch = trimmedTitleSearchText.length > 0;
  const titleSearchOptions = useMemo(
    () => ({
      text: trimmedTitleSearchText,
      fields: ['title'],
      isPublisher: fromPublisher,
    }),
    [trimmedTitleSearchText, fromPublisher],
  )
  const titleSearchResults = useDocumentSearch(titleSearchOptions);
  const titleSearchSelectorOpts = useMemo(
    () => {
      if (!isTitleSearch) {
        return undefined;
      }

      const titleSearchIds = new Set(titleSearchResults.map(({ model }) => model.id));
      return {
        filter: {
          model: {
            id: (id: string) => titleSearchIds.has(id),
          },
        },
      }
    },
    [isTitleSearch, titleSearchResults],
  )

  // [NOTE] - Could possibly refactor this to use two separate `useDocumentSearchV2` and let those handle memoization
  const { allDocsQuery, filteredDocsQuery }  = useMemo(
    () => {
      const allDocsQuery = merge(query, allDocsSelectorOpts, titleSearchSelectorOpts);

      const filteredDocsQuery = merge(query, filteredDocsSelectorOpts, titleSearchSelectorOpts);

      return { allDocsQuery, filteredDocsQuery }
    },
    [query, allDocsSelectorOpts, filteredDocsSelectorOpts, titleSearchSelectorOpts],
  )

  // [TODO] - Should attempt to not have so many instances
  const allTenantDocuments: DocumentORM[] = useAllDocumentsInstance(allDocsSelectorOpts)
  const allSearchedDocuments = useAllDocumentsInstance(allDocsQuery)
  const filteredSearchDocuments = useAllDocumentsInstance(filteredDocsQuery);
  const isFilteredSearch = isSearch || isTitleSearch;
  const allDocuments: DocumentORM[] = isFilteredSearch ? allSearchedDocuments : allTenantDocuments;
  const filteredDocuments: DocumentORM[] = useAllDocumentsInstance(filteredDocsSelectorOpts);
  const documents = isFilteredSearch ? filteredSearchDocuments : filteredDocuments;

  return {
    displayMode: isSearch ? DisplayModes.list : displayMode,
    displayModeIconName,
    toggleDisplayMode,
    selectorOpts: merge(query, filteredDocsSelectorOpts, titleSearchSelectorOpts), // used in dnapagination
    documents, // used by Tablet: quick filters, search header, filter modal, search results | Desktop: displaying number of search results
    allDocuments, // used by modal that is not visible anymore in desktop
    allTenantDocuments, // used for bulk select (important for persisting checked documents)
    isSearch,
  }
}

interface DNALibraryProps {
  toggleSearchMessage?: boolean
  disableQuickFilters?: boolean
  initUserDefaultFilters?: boolean
}

interface PaginatedContentBaseProps {
  displayMode: DisplayModes;
  selectEnabled: boolean;
  selectedDocuments: {[id: string]: boolean};
  selectedDocumentORMs: DocumentORM[];
  onSelectDocuments?: (documents: DocumentORM[]) => void;
  setSelectedDocuments: React.Dispatch<React.SetStateAction<{[id: string]: boolean}>>;
  documents: DocumentORM[];
  isSearch: boolean;
  filtersVisible: boolean;
  fixedHeaderWidth: number;
}

const DesktopPaginatedContent: React.FC<PaginatedContentBaseProps> = ({
  displayMode,
  selectEnabled,
  selectedDocuments,
  selectedDocumentORMs,
  onSelectDocuments,
  setSelectedDocuments,
  documents,
  isSearch,
  filtersVisible,
  fixedHeaderWidth,
}) => {
  const { pagedRecords } = usePagination();
  const currentDocs = pagedRecords.length > 0 ? pagedRecords : documents;
  const isCurrentPageSelected = currentDocs.length > 0 && currentDocs.every(doc => selectedDocuments[doc.model.id]);

  return (
    <DNABox as={ScrollView} appearance="col" spacing="md">
      <Iffy is={selectEnabled}>
        <DNABox
          spacing="md"
          appearance="col"
          style={[isSearch && { width: fixedHeaderWidth - (filtersVisible ? 400 : 0) }]}
        >
          <SelectedCollapsibleCard
            selectedDocuments={selectedDocumentORMs}
            onSelectDocuments={onSelectDocuments || (() => {})}
            setSelectedDocuments={setSelectedDocuments}
            displayMode={displayMode}
            isSearch={isSearch}
          />
          {displayMode === DisplayModes.grid &&
            <DNACard>
              <DNABox alignY="center" style={{ padding: 15 }}>
                <DNACheckbox
                  checked={isCurrentPageSelected}
                  onChange={onSelectDocuments ? () => onSelectDocuments(currentDocs) : undefined}
                >
                  Select All
                </DNACheckbox>
              </DNABox>
            </DNACard>
          }
        </DNABox>
      </Iffy>
      <Iffy is={displayMode === DisplayModes.list}>
        <DNADocumentGridList.Paginated
          emptyVariant={EmptyVariant.DocumentListEmptySearch}
          selectEnabled={selectEnabled}
          selectedDocuments={selectedDocuments}
          onSelectDocuments={onSelectDocuments}
          isSearch={isSearch}
          filtersVisible={filtersVisible}
          showDocumentSummaries
        />
      </Iffy>
      <Iffy is={displayMode === DisplayModes.grid}>
        <DNAGrid.Paginated
          emptyVariant={EmptyVariant.DocumentListEmptySearch}
          selectEnabled={selectEnabled}
          selectedDocuments={selectedDocuments}
          onSelectDocuments={onSelectDocuments}
        />
      </Iffy>
      <DNABox style={styles.paginationNav}>
        <DNAPagination.Nav />
      </DNABox>
    </DNABox>
  );
};

const TabletPaginatedContent: React.FC<PaginatedContentBaseProps> = ({
  displayMode,
  selectEnabled,
  selectedDocuments,
  selectedDocumentORMs,
  onSelectDocuments,
  setSelectedDocuments,
  documents,
  isSearch,
  filtersVisible,
}) => {
  const { pagedRecords } = usePagination();
  const currentDocs = pagedRecords.length > 0 ? pagedRecords : documents;
  const isCurrentPageSelected = currentDocs.length > 0 && currentDocs.every(doc => selectedDocuments[doc.model.id]);

  return (
    <ScrollView
      horizontal={displayMode === DisplayModes.list && !isSearch}
      contentContainerStyle={{ minHeight: '100%', minWidth: '100%' }}
    >
      <DNABox fill appearance="col" spacing="md" childFill={displayMode === DisplayModes.list ? 1 : 2}>
        <Iffy is={selectEnabled}>
          <DNABox appearance="col" spacing="md">
            <SelectedCollapsibleCard
              selectedDocuments={selectedDocumentORMs}
              onSelectDocuments={onSelectDocuments || (() => {})}
              setSelectedDocuments={setSelectedDocuments}
              displayMode={displayMode}
              isSearch={isSearch}
            />
          </DNABox>
          <Iffy is={displayMode === DisplayModes.grid}>
            <DNABox alignY="center" style={{ marginHorizontal: 15 }}>
              <DNACard>
                <DNABox alignY="center" style={{ padding: 20 }}>
                  <DNACheckbox
                    style={{ paddingLeft: 20 }}
                    checked={isCurrentPageSelected}
                    onChange={onSelectDocuments ? () => onSelectDocuments(currentDocs) : undefined}
                  >
                    Select All
                  </DNACheckbox>
                </DNABox>
              </DNACard>
            </DNABox>
          </Iffy>
        </Iffy>
        <Iffy is={displayMode === DisplayModes.list}>
          <DNADocumentGridList
            emptyVariant={EmptyVariant.DocumentListEmptySearch}
            documents={documents}
            selectEnabled={selectEnabled}
            selectedDocuments={selectedDocuments}
            onSelectDocuments={onSelectDocuments}
            isSearch={isSearch}
            filtersVisible={filtersVisible}
            showDocumentSummaries
          />
        </Iffy>
        <Iffy is={displayMode === DisplayModes.grid}>
          {/* CONTENT */}
          <CenteredGridLayout itemWidth={276 + 16} itemCount={documents.length}>
            <DNABox fill style={styles.mobileGridViewContainer}>
              <DNAGrid
                emptyVariant={EmptyVariant.DocumentGridEmptySearch}
                items={documents}
                selectEnabled={selectEnabled}
                selectedDocuments={selectedDocuments}
                onSelectDocuments={onSelectDocuments}
              />
            </DNABox>
          </CenteredGridLayout>
        </Iffy>
      </DNABox>
    </ScrollView>
  );
};

const paginatedContentVariants = {
  desktop: DesktopPaginatedContent,
  tablet: TabletPaginatedContent,
};

export const DNALibrary: React.FC<DNALibraryProps> = () => {
  const { searchText: encodedSearchText } = useParams<{ searchText?: string }>();
  const location = useLocation();
  const searchText = useMemo(
    () => getSearchRouteText(encodedSearchText, location.search),
    [encodedSearchText, location.search],
  );
  const { deviceMode } = useAppSettings()
  const isDesktop = deviceMode === 'desktop';
  const isTablet = deviceMode === 'tablet';
  const [titleSearchText, setTitleSearchText] = useState('');
  const {
    displayMode,
    selectorOpts,
    documents,
    allTenantDocuments,
    isSearch,
  } = useDNALibrary(searchText, undefined, titleSearchText)
  const {
    selectEnabled,
    selectedDocuments,
    setSelectedDocuments,
    onSelectDocuments,
  } = useDocumentSelectManager()
  const queryString = qs.parse(location.search, { ignoreQueryPrefix: true })

  const { isLoadingSearch, isAiSearch } = useDocumentSearchV2Context();

  const showLoader = isLoadingSearch && isSearch;

  useEffect(() => { setSelectedDocuments({} ) }, [searchText])

  const [filtersVisible, setFiltersVisible] = useState(!!queryString.quickFilter);

  const fixedHeaderWidth = useFixedHeader();
  const selectedDocumentORMs = useMemo(() => allTenantDocuments.filter(doc => selectedDocuments[doc.model.id]),
    [allTenantDocuments, selectedDocuments])

  const PaginatedContentComponent = paginatedContentVariants[deviceMode];

  if (!PaginatedContentComponent) {
    throw new Error(`PaginatedContentComponent not found for device mode: ${deviceMode}`);
  }

  const paginatedContent: ReactElement = (
    <PaginatedContentComponent
      displayMode={displayMode}
      selectEnabled={selectEnabled}
      selectedDocuments={selectedDocuments}
      selectedDocumentORMs={selectedDocumentORMs}
      onSelectDocuments={onSelectDocuments}
      setSelectedDocuments={setSelectedDocuments}
      documents={documents}
      isSearch={isSearch}
      filtersVisible={filtersVisible}
      fixedHeaderWidth={fixedHeaderWidth}
    />
  );

  return (
    <DNABox
      appearance="col"
      fill
      spacing={isDesktop ? 'md' : undefined}
      style={util.mergeStyles(
        undefined,
        [{ maxWidth: fixedHeaderWidth }, displayMode === DisplayModes.grid && isDesktop],
      )}
    >
      {!(isAiSearch && isSearch) && (
        <DNABox>
          <Header
            searchText={searchText}
            showLoader={showLoader}
            documentsCount={documents.length}
            filtersVisible={filtersVisible}
            setFiltersVisible={setFiltersVisible}
            documents={documents}
            titleSearchText={titleSearchText}
            setTitleSearchText={setTitleSearchText}
          />
        </DNABox>
      )}
      {isTablet && <DNABox style={{ height: 12 }} />}
      <DNABox
        fill
        childStyle={isDesktop && [isAiSearch && isSearch ? 0 : 1, { flex: 1 }]}
        style={{ flex: 1 }}
      >
        {isAiSearch && isSearch ? (
          <AISearchResults searchText={searchText || ''} />
        ) : (
          <>
            <ScrollView
              style={util.mergeStyles(
                undefined,
                [styles.filtersSidebarScrollView, isDesktop],
                [styles.filersSidebarNotVisible, !filtersVisible],
                [{ flexGrow: 0 }, isTablet],
              )}
            >
              <FiltersSidebar
                setVisible={setFiltersVisible}
                textValue={searchText}
                titleSearchText={titleSearchText}
              />
            </ScrollView>
            {/* PAGINATED CONTENT */}
            {showLoader ? (
              <DNABox style={{ width: fixedHeaderWidth - (filtersVisible ? 400 : 0) }}>
                <ListSkeleton numberOfItems={5} />
              </DNABox>
            ) : (
              isDesktop ? (
                <DNAPagination query={selectorOpts}>
                  {paginatedContent}
                </DNAPagination>
              ) : (
                <DNABox fill appearance="col">
                  {paginatedContent}
                </DNABox>
              )
            )}
          </>
        )}
      </DNABox>
    </DNABox>
  );
}

const DNALibrarySwitcher: React.FC<DNALibraryProps> = (props) => {
  const { quickFilters } = { ...useTenant(ActiveUser.user!.tenantId)?.tenant.config }
  useCPMPageRoute()

  return (
    <QuickFiltersProvider quickFilters={quickFilters ?? []}>
      <DocumentSelectManager>
        <DNALibrary {...props} />
      </DocumentSelectManager>
    </QuickFiltersProvider>
  )
}

export default compose<React.FC<DNALibraryProps>>(
  withDNADocumentFilters,
  withSort,
)(DNALibrarySwitcher)
